import { MODAL_UPGRADE_PLAN } from '@components/App/Modal/constants'
import type { ExportDto } from '@octopus-do/octopus-api-client'
import type { ID } from '@octopus-do/octopus-project-core'
import { ReportExportActions } from '@store/reportExport'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { openModal } from '@storeOld/reducers/ui'
import { useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useProjectAccess } from '@/hooks'
import { NestClient } from '@/nest'
import { EXPORT_PERMISSION } from '@/permissions/project/definitions'
import { createSwitch } from '@/utils'
import {
  csvFileName,
  downloadFile,
  toCsv,
  toSitemapXml,
} from '../../../../export'

// `DownloadMenu` hangs in three places — the header button, the narrow-screen
// "…" and File → Download — and each of them reaches it through a different
// path, so the cases are built around the caller's prefix and appended to
// whatever else that menu does.
//
// Both files cover the whole report, never the section and filters on screen,
// so neither can be built from the 50 rows the list is holding: each one is a
// request of its own that brings back the entire crawl.
export const useDownloadActions = (...prefix: string[]) => {
  const { uuid } = useParams<{ uuid: ID }>()
  const dispatch = useDispatch()
  const { hasAccess: exportable } = useProjectAccess(
    EXPORT_PERMISSION.FILE_FORMATS
  )

  const upgrade = () => {
    sendGtmEvent(GtmTriggerName.UPGRADE_EXPORT)
    dispatch(
      openModal({
        modalType: MODAL_UPGRADE_PLAN,
        modalProps: { planType: 'pro', slideType: 'export' },
      })
    )
  }
  // A big site takes a moment to come back, and the menu is still clickable
  // while it does — a second click would download the same file twice.
  const busy = useRef(false)

  const download = async (
    set: 'all' | 'indexed',
    write: (data: ExportDto) => void,
    event: GtmTriggerName
  ) => {
    if (!uuid || busy.current) {
      return
    }

    busy.current = true

    try {
      write(await NestClient.sitemapReport.export(uuid, { set }))
      sendGtmEvent(event)
    } catch {
      dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.SOMETHING_WENT_WRONG_ERROR,
        })
      )
    } finally {
      busy.current = false
    }
  }

  return createSwitch()
    .case([...prefix, 'pdf'], () => {
      if (!exportable) {
        upgrade()
        return
      }
      if (uuid) {
        dispatch(ReportExportActions.pdf(uuid))
      }
    })
    .case([...prefix, 'sitemap'], () => {
      // Only the indexed set, because sitemap.xml is a list of the pages a
      // search engine should keep — a page that redirects, is blocked or has
      // gone has no business in it.
      download(
        'indexed',
        (data) =>
          downloadFile(
            'sitemap.xml',
            toSitemapXml(data.items, data.crawledAt),
            'application/xml;charset=utf-8'
          ),
        GtmTriggerName.REPORT_EXPORT_SITEMAP
      )
    })
    .case([...prefix, 'csv'], () => {
      if (!exportable) {
        upgrade()
        return
      }
      // Everything, removed pages included: what a site lost since the last
      // crawl is most of what a spreadsheet of it is for.
      download(
        'all',
        (data) =>
          downloadFile(
            csvFileName(data.site.url),
            toCsv(data.items),
            'text/csv;charset=utf-8'
          ),
        GtmTriggerName.REPORT_EXPORT_CSV
      )
    })
}
